import ManageMySpending from "../ManageMySpending";
import {
  Button1,
  Button2,
  Select,
  ProgressBar,
  MultiColorBar,
  SmallCircle,
} from "../../index";
import { Input, Spin, Modal } from "antd";
import searchIcon from "../../../assets/icons/search-icon.png";
import sortIcon from "../../../assets/icons/sort-icon.png";
import plusIcon from "../../../assets/icons/plus.png";
import downIcon from "../../../assets/icons/chevron-down.png";
import deleteIcon from "../../../assets/icons/trashcan-icon.png";
import { CreditCardIcon, NoteIcon } from "../../svg-organizer/SvgOrganizer";
import { useMemo, useState } from "react";
import { VerticalBar } from "../../vertical-bar/VerticalBar";
import { useNavigate } from "react-router-dom";
import { useBudgets, useTransactions } from "../../../data/live";
import { budgetsApi } from "../../../api/endpoints";
import QuickActionSelect from "../../quick-action-select/QuickActionSelect";

const CATEGORY_COLORS = {
  Income: "#1bbb6b",
  Essentials: "#F7A64F",
  Lifestyles: "#FC555B",
  "Rent & Utilities": "#2660CE",
  Savings: "#9B59B6",
  Food: "#FFB800",
  Transport: "#1A73E8",
  Healthcare: "#3CADF1",
  Entertainment: "#922590",
  Other: "#C9C9C9",
};

const colorFor = (cat) =>
  CATEGORY_COLORS[cat] ||
  (() => {
    let h = 0;
    for (let i = 0; i < (cat || "").length; i++) h = (h * 31 + cat.charCodeAt(i)) | 0;
    return Object.values(CATEGORY_COLORS)[Math.abs(h) % Object.values(CATEGORY_COLORS).length];
  })();

const fmt = (n, ccy = "AUD") =>
  `${Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })} ${ccy}`;

const MONTHS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];

const BudgetSetup = () => {
  const [showDropDown, setShowDropDown] = useState({});
  const [search, setSearch] = useState("");
  const [editModal, setEditModal] = useState(null); // { budget, field }
  const [editValue, setEditValue] = useState("");
  const [deleting, setDeleting] = useState(null);
  const [sortOrder, setSortOrder] = useState("az");
  const navigate = useNavigate();

  const { items: budgets, loading, refresh } = useBudgets();
  const { items: transactions } = useTransactions();

  /** Group budgets by a high-level "group" inferred from category name.
   *  The backend stores individual categories like "Rent", "Groceries", etc.
   *  We group them under Income / Essentials / Lifestyles / Savings for the UI. */
  const groupedBudgets = useMemo(() => {
    const groups = {};
    const filtered = budgets.filter((b) =>
      search ? b.category.toLowerCase().includes(search.toLowerCase()) : true
    );

    for (const b of filtered) {
      // Simple grouping heuristic, user can adjust in a future settings page
      let group = "Other";
      const cat = b.category.toLowerCase();
      if (cat.includes("income") || cat.includes("salary") || cat.includes("pay"))
        group = "Income";
      else if (
        cat.includes("rent") ||
        cat.includes("utilities") ||
        cat.includes("groceries") ||
        cat.includes("essential") ||
        cat.includes("mortgage") ||
        cat.includes("loan")
      )
        group = "Essentials";
      else if (
        cat.includes("entertainment") ||
        cat.includes("travel") ||
        cat.includes("dining") ||
        cat.includes("hobbies") ||
        cat.includes("lifestyle") ||
        cat.includes("game")
      )
        group = "Lifestyles";
      else if (cat.includes("saving") || cat.includes("investment"))
        group = "Savings";

      if (!groups[group]) groups[group] = [];
      groups[group].push(b);
    }
    return groups;
  }, [budgets, search]);

  /** Monthly review, aggregate budgets for summary panel */
  const monthlyReview = useMemo(() => {
    const summary = {};
    for (const [group, items] of Object.entries(groupedBudgets)) {
      summary[group] = items.reduce((s, b) => s + Number(b.limit || 0), 0);
    }
    return summary;
  }, [groupedBudgets]);

  const totalIncome = monthlyReview["Income"] || 0;
  const totalExpenses = Object.entries(monthlyReview)
    .filter(([k]) => k !== "Income")
    .reduce((s, [, v]) => s + v, 0);
  const surplus = totalIncome - totalExpenses;

  /** Yearly overview, last 12 months of transactions */
  const yearlyIncome = useMemo(() => {
    return transactions
      .filter((t) => t.direction === "in")
      .reduce((s, t) => s + Number(t.amount || 0), 0);
  }, [transactions]);

  const yearlyExpenses = useMemo(() => {
    return transactions
      .filter((t) => t.direction === "out")
      .reduce((s, t) => s + Number(t.amount || 0), 0);
  }, [transactions]);

  /** Bar chart, monthly income from real transactions */
  const incomeByMonth = useMemo(() => {
    const buckets = Array(5).fill(0);
    const now = new Date();
    for (const t of transactions) {
      if (t.direction !== "in") continue;
      const d = new Date(t.raw?.createdAt || t.date);
      const monthsAgo = (now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth());
      if (monthsAgo >= 0 && monthsAgo < 5) {
        buckets[4 - monthsAgo] += Number(t.amount || 0);
      }
    }
    return buckets;
  }, [transactions]);

  const last5MonthLabels = useMemo(() => {
    const now = new Date();
    return Array.from({ length: 5 }, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() - (4 - i), 1);
      return MONTHS[d.getMonth()];
    });
  }, []);

  const onDropDownClick = (key) => {
    setShowDropDown((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleDelete = async (budgetId) => {
    setDeleting(budgetId);
    try {
      await budgetsApi.remove(budgetId);
      refresh();
    } catch (e) {
      console.error("Failed to delete budget:", e);
    } finally {
      setDeleting(null);
    }
  };

  const handleEditSave = async () => {
    if (!editModal) return;
    try {
      await budgetsApi.update(editModal.budget.id, { limit: editValue });
      refresh();
      setEditModal(null);
    } catch (e) {
      console.error("Failed to update budget:", e);
    }
  };

  const budgetGroupEntries = useMemo(() => {
    const entries = Object.entries(groupedBudgets);
    if (sortOrder === "az") entries.sort(([a], [b]) => a.localeCompare(b));
    else if (sortOrder === "za") entries.sort(([a], [b]) => b.localeCompare(a));
    else if (sortOrder === "amount") entries.sort(([, a], [, b]) => b.reduce((s, x) => s + Number(x.limit || 0), 0) - a.reduce((s, x) => s + Number(x.limit || 0), 0));
    return entries;
  }, [groupedBudgets, sortOrder]);

  if (loading) {
    return (
      <ManageMySpending navName={"Manage My Spending"}>
        <div className="d-flex justify-content-center py-5">
          <Spin size="large" />
        </div>
      </ManageMySpending>
    );
  }

  const reviewItems = [
    { name: "Income", color: "#1bbb6b", amount: fmt(totalIncome) },
    { name: "Essentials", color: "#F7A64F", amount: fmt(monthlyReview["Essentials"] || 0) },
    { name: "Lifestyles", color: "#FC555B", amount: fmt(monthlyReview["Lifestyles"] || 0) },
    { name: "Savings", color: "#E3E3E3", amount: fmt(monthlyReview["Savings"] || 0) },
  ];

  const multiColorBarColors = reviewItems
    .filter((r) => r.name !== "Income")
    .map((r) => r.color);

  return (
    <>
      {/* Edit Budget Modal */}
      <Modal
        title={<p className="text-center fs-5 fw-semibold">Edit Budget Limit</p>}
        centered
        width={350}
        open={!!editModal}
        onCancel={() => setEditModal(null)}
        onOk={handleEditSave}
        okText="Save"
        okButtonProps={{ className: "bg-black text-white rounded-pill" }}
        cancelButtonProps={{
          className: "rounded-pill",
          style: { borderColor: "#E7040C", color: "#E7040C" },
        }}
      >
        {editModal && (
          <div className="d-flex flex-column gap-3">
            <span className="fs14px fw-medium">{editModal.budget.category}</span>
            <Input
              className="p-3"
              type="number"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              placeholder="Budget limit"
            />
          </div>
        )}
      </Modal>

      <ManageMySpending navName={"Manage My Spending"}>
        <div className="row mt-3">
          <div className="d-flex justify-content-lg-between justify-content-center align-items-center flex-wrap  flex-lg-nowrap">
            <span className="fs-5 fw-semibold">Budget Setup</span>
            <div className="d-flex gap-2 align-items-center flex-wrap flex-lg-nowrap justify-content-center ">
              <Input
                className="rounded-pill w-auto"
                prefix={
                  <img src={searchIcon} alt="search" width={14} height={14} />
                }
                placeholder="Search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <Button2
                buttonName={sortOrder === "az" ? "Sort: A-Z" : sortOrder === "za" ? "Sort: Z-A" : sortOrder === "amount" ? "Sort: Amount" : "Sort"}
                divClassName={"gap-2"}
                className={"border"}
                icon={<img src={sortIcon} alt="sort" />}
                onClick={() => setSortOrder(prev => prev === "az" ? "za" : prev === "za" ? "amount" : "az")}
              />
              <Button2
                buttonName={"Expand All"}
                divClassName={"gap-2"}
                className={"border"}
                onClick={() => {
                  const all = {};
                  budgetGroupEntries.forEach((_, i) => { all[i] = true; });
                  setShowDropDown(all);
                }}
              />
              <Button2
                buttonName={"Create a Budget"}
                divClassName={"gap-2"}
                className={"btn-outline-success px-3 "}
                icon={<img src={plusIcon} alt="add" />}
                onClick={() => navigate("/create-budget")}
              />
              <QuickActionSelect />
            </div>
          </div>

          <div className="col-lg-8 mt-3">
            <div className="d-flex flex-column gap-3">
              {budgetGroupEntries.length === 0 ? (
                <div
                  className="text-center py-5 border rounded-3"
                  style={{ color: "#7D7D7D" }}
                >
                  No budgets yet. Click "Create a Budget" to get started.
                </div>
              ) : (
                budgetGroupEntries.map(([groupName, items], key) => {
                  const groupTotal = items.reduce(
                    (s, b) => s + Number(b.limit || 0),
                    0
                  );
                  return (
                    <div
                      key={groupName}
                      className="d-flex flex-column gap-3 border rounded-3"
                    >
                      <div className="d-flex justify-content-between align-items-center p-3 px-5 ">
                        <div className="d-flex gap-3 align-items-center">
                          <img
                            src={downIcon}
                            alt="down"
                            style={{
                              transform: showDropDown[key]
                                ? "rotate(0deg)"
                                : "rotate(-90deg)",
                            }}
                            className="cursor-pointer align-self-end"
                            onClick={() => onDropDownClick(key)}
                          />
                          <span className="fs-5 fw-semibold">{groupName}</span>
                        </div>

                        <div className="d-flex gap-2 align-items-center">
                          <span
                            className="fs14px fw-medium"
                            style={{ color: "#7D7D7D" }}
                          >
                            Budget Total
                          </span>
                          <span
                            className="fw-semibold"
                            style={{ color: "#1bbb6b" }}
                          >
                            {fmt(groupTotal, items[0]?.currency || "AUD")}
                          </span>
                        </div>
                      </div>

                      {showDropDown[key] && (
                        <div className="d-flex flex-column p-4 px-5 border-top gap-3">
                          {items.map((b, key2) => (
                            <div
                              key={b.id}
                              className={`d-flex justify-content-between align-items-center ${
                                !(key2 === items.length - 1) &&
                                "border-bottom pb-3"
                              }`}
                            >
                              <div className="d-flex gap-2 align-items-center">
                                <NoteIcon color={colorFor(b.category)} />
                                <span className="fs13px fw-semibold">
                                  {b.category}
                                </span>
                                <span
                                  className="fs12px"
                                  style={{ color: "#7D7D7D" }}
                                >
                                  ({b.period || "MONTHLY"})
                                </span>
                              </div>

                              <div className="d-flex gap-3 align-items-center">
                                <span className="fs14px fw-semibold">
                                  {fmt(b.limit, b.currency)}
                                </span>
                                {Number(b.spent || 0) > 0 && (
                                  <span
                                    className="fs12px"
                                    style={{ color: "#FC555B" }}
                                  >
                                    Spent: {fmt(b.spent, b.currency)}
                                  </span>
                                )}
                                <span
                                  className="fs12px fw-medium cursor-pointer"
                                  style={{ color: "#1bbb6b" }}
                                  onClick={() => {
                                    setEditModal({ budget: b });
                                    setEditValue(b.limit);
                                  }}
                                >
                                  Edit
                                </span>
                                <img
                                  src={deleteIcon}
                                  alt="delete"
                                  width={16}
                                  height={16}
                                  className="cursor-pointer"
                                  style={{
                                    opacity: deleting === b.id ? 0.3 : 1,
                                  }}
                                  onClick={() => handleDelete(b.id)}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <div className="col-lg-4 mt-3">
            <div className="d-flex flex-column gap-2 border rounded-3">
              <div className="d-flex flex-column gap-3 p-3">
                <span className="fs-5 fw-semibold">Monthly Review</span>

                <ProgressBar
                  label={"Income"}
                  labelClassName="fw-semibold"
                  percent={totalIncome > 0 ? 100 : 0}
                  strokeColor="#1bbb6b"
                  strokeWidth={"14px"}
                />

                <MultiColorBar
                  height="14px"
                  label={"Expenses"}
                  labelClassName={"fw-semibold"}
                  colors={multiColorBarColors}
                />
                <div className="d-flex flex-column gap-3 border-bottom pb-3">
                  {reviewItems.map(({ name, color, amount }) => (
                    <div
                      key={name}
                      className="d-flex justify-content-between align-items-center"
                    >
                      <div className="d-flex gap-2 align-items-center">
                        <SmallCircle color={color} />
                        <span className="fs12px fw-semibold">{name}</span>
                      </div>
                      <span
                        className="fs12px fw-bold"
                        style={{
                          color: `${name === "Income" ? "#1bbb6b" : "#FC555B"}`,
                        }}
                      >
                        {amount}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="d-flex justify-content-between align-items-center p-3 pt-0 border-bottom">
                <span className="fs14px fw-medium" style={{ color: "#7D7D7D" }}>
                  Surplus
                </span>
                <span className="fs14px fw-semibold">{fmt(surplus)}</span>
              </div>

              <div className="d-flex flex-column gap-3 border-bottom p-3">
                <span className="fs-5 fw-semibold">Yearly Overview</span>
                <div className="d-flex flex-column gap-2 border-bottom pb-3">
                  <span className="fs-6 fw-semibold">
                    {new Date().getFullYear()} Overview
                  </span>
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="fs14px fw-medium">Income</span>
                    <span
                      className="fs14px fw-semibold"
                      style={{ color: "#1bbb6b" }}
                    >
                      {fmt(yearlyIncome)}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="fs14px fw-medium">Expenses</span>
                    <span
                      className="fs14px fw-semibold"
                      style={{ color: "#FC555B" }}
                    >
                      {fmt(yearlyExpenses)}
                    </span>
                  </div>
                </div>
                <div className="d-flex justify-content-between align-items-center ">
                  <span
                    className="fs14px fw-medium"
                    style={{ color: "#7D7D7D" }}
                  >
                    Surplus
                  </span>
                  <span className="fs14px fw-semibold">
                    {fmt(yearlyIncome - yearlyExpenses)}
                  </span>
                </div>
              </div>

              <div className="d-flex flex-column gap-3 p-3">
                <div className="d-flex flex-column gap-1">
                  <span className="fs-5 fw-semibold">
                    Income - Pay & Salary
                  </span>
                  <span
                    className="fs12px fw-medium pe-5"
                    style={{ color: "#B0B0B0" }}
                  >
                    Average income from your last 5 months of transactions.
                  </span>
                </div>

                <div
                  className="p-3 rounded-3 d-flex justify-content-center align-items-center"
                  style={{ backgroundColor: "#F9F9F9" }}
                >
                  <VerticalBar
                    displayLegends={false}
                    displayNumberOnYAxis={false}
                    label={last5MonthLabels}
                    barChartData={[
                      {
                        label: "Income",
                        data: incomeByMonth,
                        backgroundColor: "#1bbb6b",
                        borderRadius: 50,
                        borderSkipped: false,
                        barThickness: 20,
                      },
                    ]}
                  />
                </div>

                <Button1
                  buttonName={"View Transaction"}
                  className={"btn-outline-success m-3"}
                  onClick={() => navigate("/my-transaction")}
                />
              </div>
            </div>
          </div>
        </div>
      </ManageMySpending>
    </>
  );
};

export default BudgetSetup;
